// @ts-check

// Vendor imports
const React = require('react')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')

// Constants

const THEME = {
  LIGHT: 'light',
  DARK: 'dark'
}

const THEME_CLASS = {
  LIGHT: 'theme--light',
  DARK: 'theme--dark'
}

// React Contexts

const context = React.createContext({
  theme: undefined,
  setTheme: () => {}
})

// React Elements

/**
 * React Element that acts as the theme context provider
 * @param {Object} props
 * @param {React.ReactChildren} props.children
 * @returns {React.ReactElement}
 */
function ThemeManager(props) {
  const [ theme, setTheme ] = React.useState()
  useSetInitialThemeEffect(setTheme)
  useSetThemeMediaQueryListener(setTheme)
  useThemeChangedEffect(theme)
  return (
    tag(context.Provider, {
      value: { theme, setTheme }
    },
      props.children
    )
  )
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  ThemeManager.propTypes = {
    children: PropTypes.node
  }
}

// React Hooks

/**
 * React Hook that inspects <html> to figure out the initial theme.
 * @param {React.Dispatch<React.SetStateAction<string>>} setTheme
 * @returns {void}
 * @see {@link https://github.com/mix/mix/blob/master/app/views/partials/scripts/theme.pug}
 */
function useSetInitialThemeEffect(setTheme) {
  if (typeof document === 'undefined') {
    return
  }
  React.useEffect(
    () => {
      const { classList } = document.documentElement
      if (classList.contains(THEME_CLASS.LIGHT)) {
        setTheme(THEME.LIGHT)
      } else if (classList.contains(THEME_CLASS.DARK)) {
        setTheme(THEME.DARK)
      }
    },
    [ setTheme ]
  )
}

/**
 * React Hook that listens for system/user-agent theme changes
 * @param {React.Dispatch<React.SetStateAction<string>>} setTheme
 * @returns {void}
 */
function useSetThemeMediaQueryListener(setTheme) {
  if (typeof window === 'undefined') {
    return
  }
  const listener = React.useCallback(
    event => { setTheme(event.matches ? THEME.DARK : THEME.LIGHT) },
    [ setTheme ]
  )
  React.useEffect(
    () => {
      const mql = window.matchMedia('(prefers-color-scheme: dark)')
      mql.addListener(listener)
      return () => {
        mql.removeListener(listener)
      }
    },
    [ listener ]
  )
}

/**
 * React Hook that updates <html> classes based on the current theme.
 * @param {string} theme
 * @returns {void}
 */
function useThemeChangedEffect(theme) {
  if (typeof document === 'undefined') {
    return
  }
  React.useEffect(
    () => {
      const { classList } = document.documentElement
      if (theme === THEME.LIGHT) {
        classList.remove(THEME_CLASS.DARK)
        classList.add(THEME_CLASS.LIGHT)
      }
      if (theme === THEME.DARK) {
        classList.remove(THEME_CLASS.LIGHT)
        classList.add(THEME_CLASS.DARK)
      }
    },
    [ theme ]
  )
}

/**
 * React Hook that gets the current theme from the context.
 * @returns {string}
 */
function useTheme() {
  const { theme } = React.useContext(context)
  return theme
}

// Exports

module.exports = {
  ThemeManager,
  useTheme
}
